/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo } from "react";
import ParentSize from "@visx/responsive/lib/components/ParentSize";
import { scaleLinear, scaleBand } from "@visx/scale";
import { Bar } from "@visx/shape";
import { LinePath } from "@visx/shape";
import { Group } from "@visx/group";
import { AxisLeft, AxisRight, AxisBottom } from "@visx/axis";
import { extent } from "@visx/vendor/d3-array";
import CustomDropdown from "../dropdown/CustomDropDown";

// Reuse TherapyDataPoint shape from TherapySetVsDelivered
export interface SessionCycleData {
  cycle: number;
  setPausePressure: number;
  setInspPressure: number;
  setExpPressure: number;
  peakInspPressure: number;
  flow: number;
  deliveredVolume: number;
}

interface SessionPressureChartProps {
  cycles: SessionCycleData[];
  selectedMetric: "flow" | "volume";
  onMetricChange: (metric: "flow" | "volume") => void;
}

const COLORS = {
  setPause: "rgba(168,224,95,0.6)",   // #A8E05F
  setInsp: "rgba(31,183,227,0.6)",    // #1FB7E3
  setExp: "rgba(255,143,28,0.6)",     // #FF8F1C
  peakInsp: "#15803d",                // Dark green
  flow: "rgba(84,108,255,1)",         // Bright blue
  volume: "#ba4bbaff",               // Purple
};

const METRIC_OPTIONS = [
  { label: "Exp. Flow", value: "flow" },
  { label: "Exp. Volume", value: "volume" },
];

const MARGIN = { top: 30, right: 60, bottom: 50, left: 50 };

const SessionPressureChartInner: React.FC<
  SessionPressureChartProps & { width: number }
> = ({ cycles, selectedMetric, onMetricChange, width }) => {
  // Sort cycles by cycle number
  const sortedCycles = useMemo(
    () => [...cycles].sort((a, b) => a.cycle - b.cycle),
    [cycles]
  );

  // Compute data ranges to determine dynamic chart height
  const positivePressureValues = useMemo(
    () =>
      sortedCycles.flatMap((d) => [
        d.setPausePressure,
        d.setInspPressure,
        d.peakInspPressure,
      ]),
    [sortedCycles]
  );

  const expPressureValues = useMemo(
    () => sortedCycles.map((d) => d.setExpPressure),
    [sortedCycles]
  );

  const secondaryValues = useMemo(
    () =>
      sortedCycles.map((d) =>
        selectedMetric === "flow" ? d.flow : d.deliveredVolume
      ),
    [sortedCycles, selectedMetric]
  );

  const maxPositive = useMemo(() => {
    const ext = extent(positivePressureValues) as [number, number];
    return Math.max((ext[1] || 0) * 1.15, 10);
  }, [positivePressureValues]);

  const maxNegative = useMemo(() => {
    const ext = extent(expPressureValues) as [number, number];
    return Math.min((ext[0] || 0) * 1.15, -5);
  }, [expPressureValues]);

  const secondaryExtent = useMemo(
    () => extent(secondaryValues) as [number, number],
    [secondaryValues]
  );

  const maxSecondary = Math.max((secondaryExtent[1] || 0) * 1.15, 10);

  // Dynamic chart height: allocate pixels proportionally
  // Top portion for positive pressure, bottom for negative pressure + flow/volume
  const positiveRange = maxPositive;
  const negativeRange = Math.abs(maxNegative) + maxSecondary;
  const totalRange = positiveRange + negativeRange;
  const MIN_HEIGHT = 220;
  const MAX_HEIGHT = 320;
  const chartHeight = Math.min(
    MAX_HEIGHT,
    Math.max(MIN_HEIGHT, Math.round(totalRange * 2.5))
  );

  const innerWidth = width - MARGIN.left - MARGIN.right;
  const innerHeight = chartHeight - MARGIN.top - MARGIN.bottom;

  // X scale - band scale for cycle numbers
  const xScale = useMemo(
    () =>
      scaleBand<number>({
        domain: sortedCycles.map((d) => d.cycle),
        range: [0, innerWidth],
        padding: 0.3,
      }),
    [sortedCycles, innerWidth]
  );

  // Left Y scale - Pressure (cmH2O) — full range, 0 positioned at zeroY
  const yPressureScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: [maxNegative, maxPositive],
        range: [innerHeight, 0],
        nice: true,
      }),
    [maxNegative, maxPositive, innerHeight]
  );

  // Compute actual zero position from pressure scale
  const pressureZeroY = yPressureScale(0);

  // Right Y scale - Flow or Volume — below zero line, max at bottom
  const ySecondaryScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: [0, maxSecondary],
        range: [pressureZeroY, innerHeight],
        nice: true,
      }),
    [maxSecondary, pressureZeroY, innerHeight]
  );

  // Mirrored right Y scale — above zero line (positive labels going up)
  const ySecondaryUpperScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: [0, maxSecondary],
        range: [pressureZeroY, Math.max(pressureZeroY - (innerHeight - pressureZeroY), 0)],
        nice: true,
      }),
    [maxSecondary, pressureZeroY, innerHeight]
  );

  // Bar group setup - positive bars (Pause, Insp) + Exp bar separately
  const posBarKeys = ["setPausePressure", "setInspPressure"] as const;
  const posBarColors = [COLORS.setPause, COLORS.setInsp];
  const bandwidth = xScale.bandwidth();
  const barWidth = Math.max(bandwidth / 3 - 1, 2);

  if (sortedCycles.length === 0) {
    return (
      <div className="flex items-center justify-center h-[250px] text-gray-400 text-sm">
        No cycle data available for this session.
      </div>
    );
  }

  return (
    <div>
      {/* Legend + Dropdown row */}
      <div className="flex flex-wrap items-center gap-3 text-xs mb-1 mt-1">
        <span className="flex items-center gap-1">
          <span
            className="inline-block w-3 h-3 rounded-sm"
            style={{ backgroundColor: COLORS.setPause }}
          />
          Pause Pressure
        </span>
        <span className="flex items-center gap-1">
          <span
            className="inline-block w-3 h-3 rounded-sm"
            style={{ backgroundColor: COLORS.setInsp }}
          />
          Insp. Pressure
        </span>
        <span className="flex items-center gap-1">
          <span
            className="inline-block w-3 h-3 rounded-sm"
            style={{ backgroundColor: COLORS.setExp }}
          />
          Exp. Pressure
        </span>
        <span className="flex items-center gap-1">
          <span
            className="inline-block w-3 h-3 rounded-full"
            style={{ backgroundColor: COLORS.peakInsp }}
          />
          Peak Insp. Pressure
        </span>
        <span className="flex items-center gap-1">
          <span
            className="inline-block w-3 h-3 rounded-full"
            style={{
              backgroundColor:
                selectedMetric === "flow" ? COLORS.flow : COLORS.volume,
            }}
          />
          {selectedMetric === "flow" ? "Peak Exp Flow" : "Exp Volume"}
        </span>
        <span className="ml-auto">
          <CustomDropdown
            options={METRIC_OPTIONS}
            selected={selectedMetric}
            onChange={(v) => onMetricChange(v as "flow" | "volume")}
            minWidth={120}
          />
        </span>
      </div>

      <svg width={width} height={chartHeight} overflow="visible" style={{ overflow: "visible" }}>
        <defs>
          <clipPath id="chart-area-clip">
            <rect x={0} y={0} width={innerWidth} height={innerHeight} />
          </clipPath>
        </defs>
        <Group left={MARGIN.left} top={MARGIN.top}>
          {/* Left Y Axis - Pressure */}
          <AxisLeft
            scale={yPressureScale}
            numTicks={5}
            label="Pressure (cmH₂O)"
            labelProps={{
              fontSize: 10,
              textAnchor: "middle",
              fill: "#666",
            }}
            tickLabelProps={() => ({
              fontSize: 9,
              textAnchor: "end",
              fill: "#666",
              dx: -4,
            })}
            stroke="#ccc"
            tickStroke="#ccc"
          />

          {/* Right Y Axis - Flow/Volume (below zero) */}
          <AxisRight
            scale={ySecondaryScale}
            left={innerWidth}
            numTicks={3}
            tickFormat={(v) => `-${Math.abs(Number(v))}`}
            labelProps={{
              fontSize: 10,
              textAnchor: "middle",
              fill:
                selectedMetric === "flow" ? COLORS.flow : COLORS.volume,
            }}
            tickLabelProps={() => ({
              fontSize: 9,
              textAnchor: "start",
              fill:
                selectedMetric === "flow" ? COLORS.flow : COLORS.volume,
              dx: 4,
            })}
            stroke="#ccc"
            tickStroke="#ccc"
          />

          {/* Right Y Axis - Flow/Volume (above zero, positive labels) */}
          <AxisRight
            scale={ySecondaryUpperScale}
            left={innerWidth}
            numTicks={3}
            tickFormat={(v) => (Number(v) === 0 ? "" : `${Math.abs(Number(v))}`)}
            tickLabelProps={() => ({
              fontSize: 9,
              textAnchor: "start",
              fill:
                selectedMetric === "flow" ? COLORS.flow : COLORS.volume,
              dx: 4,
            })}
            stroke="transparent"
            tickStroke="#ccc"
          />

          {/* Y2 label rendered manually for reliable bottom-to-top rotation */}
          <text
            transform={`translate(${innerWidth + 50}, ${pressureZeroY / 2}) rotate(-90)`}
            textAnchor="end"
            fontSize={10}
            fill={selectedMetric === "flow" ? COLORS.flow : COLORS.volume}
          >
            {selectedMetric === "flow" ? "Peak Exp Flow (L/min)" : "Exp Volume (mL)"}
          </text>

          {/* Zero line */}
          <line
            x1={0}
            x2={innerWidth}
            y1={pressureZeroY}
            y2={pressureZeroY}
            stroke="#999"
            strokeWidth={1}
          />

          {/* Bottom X Axis - Cycle numbers */}
          <AxisBottom
            scale={xScale}
            top={innerHeight}
            label="Cycle"
            labelProps={{
              fontSize: 10,
              textAnchor: "middle",
              fill: "#666",
            }}
            tickLabelProps={() => ({
              fontSize: 9,
              textAnchor: "middle",
              fill: "#666",
            })}
            stroke="#ccc"
            tickStroke="#ccc"
          />

          {/* Data elements clipped to chart bounds */}
          <g clipPath="url(#chart-area-clip)">

          {/* Positive pressure bars (Pause, Insp) — grow upward from zero */}
          {sortedCycles.map((d) => {
            const x0 = xScale(d.cycle) ?? 0;
            return posBarKeys.map((key, i) => {
              const value = d[key];
              const yTop = yPressureScale(value);
              const barHeight = pressureZeroY - yTop;
              return (
                <Bar
                  key={`bar-${d.cycle}-${key}`}
                  x={x0 + i * (barWidth + 1)}
                  y={yTop}
                  width={barWidth}
                  height={Math.max(barHeight, 0)}
                  fill={posBarColors[i]}
                  rx={1}
                />
              );
            });
          })}

          {/* Exp pressure bars — grow downward from zero (negative values) */}
          {sortedCycles.map((d) => {
            const x0 = xScale(d.cycle) ?? 0;
            const value = d.setExpPressure;
            const yBottom = yPressureScale(value);
            const barHeight = yBottom - pressureZeroY;
            return (
              <Bar
                key={`bar-exp-${d.cycle}`}
                x={x0 + 2 * (barWidth + 1)}
                y={pressureZeroY}
                width={barWidth}
                height={Math.max(barHeight, 0)}
                fill={COLORS.setExp}
                rx={1}
              />
            );
          })}

          {/* Peak Insp. Pressure line + horizontal tick marks */}
          <LinePath
            data={sortedCycles}
            x={(d) => (xScale(d.cycle) ?? 0) + bandwidth / 2}
            y={(d) => yPressureScale(d.peakInspPressure)}
            stroke={COLORS.peakInsp}
            strokeWidth={1.5}
          />
          {sortedCycles.map((d) => {
            const cx = (xScale(d.cycle) ?? 0) + bandwidth / 2;
            const cy = yPressureScale(d.peakInspPressure);
            const tickW = bandwidth * 0.35;
            return (
              <line
                key={`tick-peak-${d.cycle}`}
                x1={cx - tickW}
                x2={cx + tickW}
                y1={cy}
                y2={cy}
                stroke={COLORS.peakInsp}
                strokeWidth={3}
                strokeLinecap="round"
              />
            );
          })}

          {/* Secondary metric line (flow or volume) + dots */}
          <LinePath
            data={sortedCycles}
            x={(d) => (xScale(d.cycle) ?? 0) + bandwidth / 2}
            y={(d) =>
              ySecondaryScale(
                selectedMetric === "flow" ? d.flow : d.deliveredVolume
              )
            }
            stroke={
              selectedMetric === "flow" ? COLORS.flow : COLORS.volume
            }
            strokeWidth={1.5}
          />
          {sortedCycles.map((d) => {
            const val =
              selectedMetric === "flow" ? d.flow : d.deliveredVolume;
            return (
              <circle
                key={`dot-secondary-${d.cycle}`}
                cx={(xScale(d.cycle) ?? 0) + bandwidth / 2}
                cy={ySecondaryScale(val)}
                r={3}
                fill={
                  selectedMetric === "flow" ? COLORS.flow : COLORS.volume
                }
              />
            );
          })}

          </g>
        </Group>
      </svg>
    </div>
  );
};

const SessionPressureChart: React.FC<SessionPressureChartProps> = (props) => {
  if (props.cycles.length === 0) {
    return (
      <div className="flex items-center justify-center h-[250px] text-gray-400 text-sm">
        No cycle data available for this session.
      </div>
    );
  }

  return (
    <div>
      <ParentSize>
        {({ width }) =>
          width > 0 ? (
            <SessionPressureChartInner {...props} width={width} />
          ) : null
        }
      </ParentSize>
    </div>
  );
};

export default SessionPressureChart;
